import { FC, useEffect, useRef, useState } from 'react';

import { useChannels } from '@mint-lib/hooks';
import styled, { css, isPropValid, type Theme } from '@mint-lib/styled';

import { Icon, Tooltip } from '@myn/mui';
import { rem } from '@myn/shared/src/utils';

import {
  BUILDER_ROW_TYPES,
  VERSION_TABLE_ROW_DEPTH,
} from '../../../../../../constants/campaignBuilder.js';
import { VERSION_ROW_TYPES } from '../../../../../../constants/versionsColumnsObject.js';
import useGetPlatformIntegrations from '../../../../../../hooks/useGetPlatformIntegrations.js';
import { useTableScale } from '../../../../../../hooks/useTableScale.js';
import { useTextOverflow } from '../../../../../../hooks/useTextOverflow.js';
import {
  VersionsTableRow,
  VersionTableColumn,
} from '../../../../../../types/versions.js';
import {
  mapRowTypeToFontSize,
  mapRowTypeToFontWeight,
} from '../../../utils/index.js';

type Props = {
  row: VersionsTableRow;
  column: VersionTableColumn;
  cellColor: string;
};

const VersionsNameField: FC<Props> = ({ row, column, cellColor }) => {
  const scale = useTableScale();
  const { platformByName } = useGetPlatformIntegrations();
  const channelsMeta = useChannels();

  const textElementRef = useRef<HTMLDivElement | null>(null);

  const isOverflowed = useTextOverflow({
    textElementRef,
    withVerticalOverflow: true,
  });

  const [isInitialOpened, setIsInitialOpened] = useState<boolean>(false);

  const { type, label, name, isCurrentVersion } = row.original;

  const isVersionType = type === BUILDER_ROW_TYPES.VERSION;

  useEffect(() => {
    if (isInitialOpened && !column.isInitialOpened) {
      setIsInitialOpened(false);
      return;
    }
    if (column.isInitialOpened) {
      setIsInitialOpened(true);
    }
  }, [column.isInitialOpened]);

  const renderLogo = () => {
    if (row.depth === VERSION_TABLE_ROW_DEPTH.VERSION) {
      return null;
    }

    if (row.depth === VERSION_TABLE_ROW_DEPTH.CHANNEL) {
      return (
        <Icon
          component={channelsMeta[name]?.icon ?? 'Settings'}
          sx={{ scale: 1.25 * scale }}
        />
      );
    }

    if (row.depth === VERSION_TABLE_ROW_DEPTH.PLATFORM) {
      const platformLogo = platformByName[name].logo;
      return platformLogo ? (
        <RowIcon src={platformLogo} $scale={scale} />
      ) : (
        <Icon component="Settings" sx={{ scale: 1.25 * scale }} />
      );
    }

    return null;
  };

  const renderExpandIcon = () => {
    if (!row.canExpand) {
      return null;
    }

    return (
      <Icon
        component={row.isExpanded ? 'ChevronUp' : 'ChevronDown'}
        sx={{
          scale: 1.25 * scale,
        }}
      />
    );
  };

  return (
    <Wrapper $scale={scale} depth={row.depth}>
      <ExpandedBlock
        depth={row.depth}
        {...row.getToggleRowExpandedProps({ title: null })}
        $scale={scale}
        type={type}
      >
        <Tooltip
          placement="top"
          arrow
          title={label ?? ''}
          disableHoverListener={!isOverflowed}
        >
          <TitleWrapper
            $color={cellColor}
            $isTextMarked={isCurrentVersion}
            $isTextTruncated={isVersionType}
            name={name || ''}
            $depth={row.depth}
            ref={textElementRef}
          >
            {renderLogo()}
            {label}
          </TitleWrapper>
        </Tooltip>
        {renderExpandIcon()}
      </ExpandedBlock>
    </Wrapper>
  );
};

const Wrapper = styled('div', { shouldForwardProp: isPropValid })<{
  depth: number;
  $scale: number;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  overflow: hidden;

  ${({ depth, $scale, theme }) =>
    depth &&
    css`
      ::before {
        position: absolute;
        left: ${32 * $scale}px;
        content: ' ';
        height: 100%;
        width: 1px;
        background-color: ${theme.palette.border.secondary};
      }

      ::after {
        position: absolute;
        left: ${32 * $scale}px;
        top: 50%;
        content: '';
        height: 1px;
        width: ${depth * 16 * $scale}px;
        background-color: ${theme.palette.border.secondary};
      }
    `}
`;

const ExpandedBlock = styled('div', { shouldForwardProp: isPropValid })<{
  depth: number;
  $scale: number;
  type: VERSION_ROW_TYPES;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  padding-left: ${({ depth, $scale }) => {
    if (!depth) {
      return 8 * $scale;
    }
    return (depth === 2 ? 75 : 56) * $scale;
  }}px;
  ${({ $scale, type }) => css`
    padding-right: ${16 * $scale}px;
    font-weight: ${mapRowTypeToFontWeight(type)};
    font-size: ${$scale * mapRowTypeToFontSize(type)}rem;
    line-height: ${rem(20 * $scale)};
  `}
  color: ${({ theme }) => theme.palette.text.secondary};
  text-transform: capitalize;
`;

const TruncatedTextCss = (theme: Theme) => css`
  padding: 0 ${theme.spacing(1)};
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
`;

const TitleWrapper = styled('div')<{
  $color: string;
  $isTextMarked: boolean;
  $isTextTruncated: boolean;
  name: string;
  $depth: number;
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  text-transform: capitalize;
  text-align: left;
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: 6px;
  background-color: ${({ $color }) => $color};

  ${({ $isTextMarked, theme }) => css`
    color: ${$isTextMarked
      ? theme.palette.dataVisualization.emerald600
      : theme.palette.text.secondary};
  `}
  ${({ $isTextTruncated, theme }) =>
    $isTextTruncated && TruncatedTextCss(theme)}
`;

const RowIcon = styled('img')<{ $scale: number }>`
  ${({ $scale }) => css`
    width: ${24 * $scale}px;
    height: ${24 * $scale}px;
  `}
`;

export default VersionsNameField;
